/* eslint-disable complexity */
import {
  Box,
  Center,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  faCaretDown,
  faCaretRight,
  faCheckCircle,
  faChevronDown,
  faCircleDot,
  faCogs,
  faCube,
  faDrawPolygon,
  faEllipsis,
  faEye,
  faEyeSlash,
  faObjectGroup,
  faObjectUngroup,
  faPen,
  faPencil,
  faTimesCircle,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import React, {
  memo,
  MutableRefObject,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PolylineIcon } from '../../assets/icons/draw-polyline.svg';
import SparkelIcon from '../common/icon/SparkelIcon';
import { ViewerMode } from '../common/viewer-mode';
import { useDiffing } from '../project/project-history/hooks/useDiffing';
import { DiffBadge } from '../project/project-history/DiffBadge';
import { DiffText } from '../project/project-history/DiffText';
import { EditAutoshapePopover } from './EditAutoshapePopover';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';
import {
  AutoShapeDeepFragment,
  AutoShapeStatus,
  EventType,
  ShapeDeepFragment,
  ShapeFolderDeepFragment,
  SheetShapeDeepFragment,
} from 'src/gql/graphql';
import { ChakraColor } from 'src/utils/color-util';
import { useUserTenant } from 'src/services/auth-info';

export type ShapeNodeType =
  | ShapeDeepFragment
  | SheetShapeDeepFragment
  | ShapeFolderDeepFragment
  | AutoShapeDeepFragment;

export type ShapeItemDiff = {
  eventType?: EventType;
  oldName?: string;
  newName?: string;
};

type ShapeItemProps = {
  node: ShapeNodeType;
  expanded?: boolean;
  setExpanded?: (shapedId: string) => void;
  isSelected: boolean;
  allChildrenSelected?: boolean;
  allChildrenHidden?: boolean;
  onShapeSelect: (node: ShapeNodeType) => void;
  shapeChildren?: ShapeItemProps[];
  hasChildren?: boolean;
  top?: number;
  height?: string | number;
  onMoveNode: (
    draggedNodeIds: (ShapeDeepFragment | SheetShapeDeepFragment)[],
    targetNode: ShapeFolderDeepFragment | null
  ) => void;
  onRename: (node: ShapeNodeType, newName: string) => void;
  onShow: (node: ShapeNodeType) => void;
  onHide: (node: ShapeNodeType) => void;
  onDelete: (node: ShapeNodeType) => void;
  level?: number;
  isHidden: boolean;
  setPageNumber: (pageNumber: number) => void;
  changeActiveSheet: (sheetId: string) => void;
  editBIMShape: (shapeToEdit: string) => void;
  editSheetShape: (shapeToEdit: string) => void;
  setViewerMode: (mode: ViewerMode) => void;
  removeShapesFromFolder: (node: ShapeNodeType) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  groupShapesIntoFolder: () => Promise<void>;
  selectedShapesRef: MutableRefObject<
    (ShapeDeepFragment | SheetShapeDeepFragment)[]
  >;
  isDiffing?: boolean;
  diff?: ShapeItemDiff;
};

// eslint-disable-next-line complexity
function ShapeItem({
  node,
  expanded,
  setExpanded,
  isSelected,
  allChildrenSelected,
  allChildrenHidden,
  onShapeSelect,
  onMoveNode,
  onRename,
  onShow,
  onHide,
  onDelete,
  shapeChildren,
  level = 0,
  isHidden,
  hasChildren,
  setPageNumber,
  changeActiveSheet,
  editBIMShape,
  editSheetShape,
  setViewerMode,
  removeShapesFromFolder,
  deleteFolder,
  groupShapesIntoFolder,
  selectedShapesRef,
  top,
  height,
  isDiffing,
  diff,
}: ShapeItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(node.name);

  const ExpanderButton = (
    <IconButton
      aria-label="Expand"
      isDisabled={!hasChildren}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded?.(node.id);
      }}
      icon={
        <SparkelIcon
          fixedWidth
          icon={expanded ? faCaretDown : faCaretRight}
          size="2xs"
        />
      }
      size={'2xs'}
      variant={'unstyled'}
      paddingLeft={0}
      paddingRight={1}
    />
  );

  const isLeafNode = !shapeChildren;

  let nodeLabel =
    isDiffing && diff?.oldName && diff?.newName ? (
      <DiffText oldValue={diff?.oldName} newValue={diff?.newName} />
    ) : (
      node.name
    );

  if (isDiffing && diff?.eventType === EventType.Deleted) {
    nodeLabel = <Text textDecoration="line-through">{node.name}</Text>;
  }

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      const found = selectedShapesRef.current.find(
        (shape) => shape.id === node.id
      );
      const shapesToMove = found
        ? selectedShapesRef.current // If the shape is selected, drag the entire selection
        : [node]; // Otherwise, just drag the single node

      e.dataTransfer.setData('text/plain', JSON.stringify(shapesToMove));
      e.dataTransfer.effectAllowed = 'move';
    },
    [node, selectedShapesRef]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsHovered(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const draggedShapes = JSON.parse(e.dataTransfer.getData('text/plain')) as
        | (ShapeDeepFragment | SheetShapeDeepFragment)[];

      // Move all shapes in the selection to the new folder (or out of the folder)
      onMoveNode(
        draggedShapes,
        !isLeafNode ? (node as ShapeFolderDeepFragment) : null
      );

      setIsHovered(false);
    },
    [isLeafNode, node, onMoveNode]
  );

  const folderOutline =
    isHovered && !isLeafNode ? `2px solid ${ChakraColor.blue300}` : 'none';

  return (
    <Box
      width={'100%'}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      outline={folderOutline}
      outlineOffset={-2}
      borderRadius="md"
      position="absolute"
      // I have to put these properties in the style object otherwise if I pass them as direct Box props, chakra interprets them in a special way
      style={{ transform: `translateY(${top}px)`, height: height }}
    >
      {!isLeafNode ? (
        <>
          <NodeItemMemoized
            node={node}
            key={node.id}
            isHidden={isHidden}
            setPageNumber={setPageNumber}
            changeActiveSheet={changeActiveSheet}
            editBIMShape={editBIMShape}
            editSheetShape={editSheetShape}
            isSelected={isSelected}
            allChildrenSelected={allChildrenSelected}
            allChildrenHidden={allChildrenHidden}
            onSelect={onShapeSelect}
            level={level}
            isRenaming={isRenaming}
            setIsRenaming={setIsRenaming}
            onShow={onShow}
            onHide={onHide}
            onDelete={onDelete}
            setViewerMode={setViewerMode}
            removeShapesFromFolder={removeShapesFromFolder}
            deleteFolder={deleteFolder}
            groupShapesIntoFolder={groupShapesIntoFolder}
            isDiffing={isDiffing}
            diff={diff}
          >
            {ExpanderButton}
            {isRenaming ? (
              <Input
                value={newName}
                size="xs"
                width="100%"
                height={5}
                marginLeft={1}
                autoFocus
                variant={'filled'}
                borderRadius={0}
                onChange={(e) => setNewName(e.target.value)}
                onBlur={() => {
                  setIsRenaming(false);
                  if (newName !== node.name) {
                    onRename(node, newName);
                  }
                }}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setIsRenaming(false);
                    if (newName !== node.name) {
                      onRename(node, newName);
                    }
                  }
                }}
              />
            ) : (
              <Text
                paddingLeft={2}
                wordBreak={'break-all'}
                noOfLines={1}
                opacity={!hasChildren ? 0.5 : 1}
              >
                {nodeLabel}
              </Text>
            )}
          </NodeItemMemoized>
          {expanded && (
            <>
              {shapeChildren?.map((child) => {
                return (
                  <MemoizedShapeItem
                    key={child.node.id}
                    {...child}
                    level={level + 1}
                  />
                );
              })}
            </>
          )}
        </>
      ) : (
        <NodeItemMemoized
          key={node.id}
          node={node}
          isSelected={isSelected}
          allChildrenSelected={allChildrenSelected}
          allChildrenHidden={allChildrenHidden}
          isHidden={isHidden}
          setPageNumber={setPageNumber}
          changeActiveSheet={changeActiveSheet}
          editBIMShape={editBIMShape}
          editSheetShape={editSheetShape}
          onSelect={onShapeSelect}
          level={level}
          setViewerMode={setViewerMode}
          removeShapesFromFolder={removeShapesFromFolder}
          deleteFolder={deleteFolder}
          isMoveable={
            node.__typename === 'Shape' || node.__typename === 'SheetShape'
          } // Shapes are draggable
          onDragStart={handleDragStart} // Handle drag start only for shapes
          isRenaming={isRenaming}
          setIsRenaming={setIsRenaming}
          onShow={onShow}
          onHide={onHide}
          onDelete={onDelete}
          groupShapesIntoFolder={groupShapesIntoFolder}
          isDiffing={isDiffing}
          diff={diff}
        >
          <IconFromShape shape={node} />
          {isRenaming ? (
            <Input
              value={newName}
              size="xs"
              width="100%"
              height={5}
              marginLeft={1}
              autoFocus
              variant={'filled'}
              borderRadius={0}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={() => {
                setIsRenaming(false);
                if (newName !== node.name) {
                  onRename(node, newName);
                }
              }}
              onFocus={(e) => e.target.select()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsRenaming(false);
                  if (newName !== node.name) {
                    onRename(node, newName);
                  }
                }
              }}
            />
          ) : (
            <Text marginLeft={3} wordBreak={'break-all'} noOfLines={1}>
              {nodeLabel}
            </Text>
          )}
        </NodeItemMemoized>
      )}
    </Box>
  );
}

type NodeItemProps = {
  node: ShapeNodeType;
  isSelected: boolean;
  allChildrenSelected?: boolean;
  allChildrenHidden?: boolean;
  onSelect: (node: ShapeNodeType) => void;
  children?: React.ReactNode;
  level: number;
  isMoveable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  isRenaming: boolean;
  setIsRenaming: (isRenaming: boolean) => void;
  onShow: (node: ShapeNodeType) => void;
  onHide: (node: ShapeNodeType) => void;
  onDelete: (node: ShapeNodeType) => void;
  isHidden: boolean;
  setPageNumber: (pageNumber: number) => void;
  changeActiveSheet: (sheetId: string) => void;
  editBIMShape: (shapeToEdit: string) => void;
  editSheetShape: (shapeToEdit: string) => void;
  setViewerMode: (mode: ViewerMode) => void;
  removeShapesFromFolder: (node: ShapeNodeType) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  groupShapesIntoFolder: () => Promise<void>;
  isDiffing?: boolean;
  diff?: ShapeItemDiff;
};

// eslint-disable-next-line complexity
const NodeItem = ({
  node,
  isSelected,
  allChildrenSelected,
  isHidden,
  onSelect,
  children,
  level,
  isMoveable = false,
  onDragStart,
  isRenaming,
  setIsRenaming,
  onShow,
  onHide,
  onDelete,
  setPageNumber,
  changeActiveSheet,
  editBIMShape,
  editSheetShape,
  setViewerMode,
  removeShapesFromFolder,
  deleteFolder,
  groupShapesIntoFolder,
  isDiffing,
  diff,
}: NodeItemProps) => {
  const isAutoShapeChild =
    !!(node as ShapeDeepFragment).autoShapeId ||
    !!(node as SheetShapeDeepFragment).autoShapeId;
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();
  const menuItemColor = useColorModeValue('gray.800', 'gray.200');
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const selectedNodeBackgroundColor = useColorModeValue('blue.50', 'blue.800');
  const autoShapeColor = useColorModeValue('teal.500', 'teal.200');
  const selectedAutoShapeBackgroundColor = useColorModeValue(
    'teal.50',
    'teal.800'
  );
  const selectedColor = useColorModeValue('blue.700', 'blue.200');
  const selectedParentBackgroundColor = useColorModeValue(
    'blue.100',
    'blue.600'
  );
  const deleteColor = useColorModeValue('red.400', 'red.300');
  const { getTextColor, getBackgroundColor } = useDiffing();
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);

  let selectedBackgroundColor = selectedNodeBackgroundColor;
  let nodeColor = menuItemColor;

  if (node.__typename === 'AutoShape' || isAutoShapeChild) {
    selectedBackgroundColor = selectedAutoShapeBackgroundColor;
    nodeColor = autoShapeColor;
  } else if (
    node.__typename === 'ShapeFolder' ||
    node.__typename === undefined
  ) {
    selectedBackgroundColor = selectedParentBackgroundColor;
  }

  if (
    allChildrenSelected &&
    !isSelected &&
    !isAutoShapeChild &&
    node.__typename !== 'AutoShape'
  ) {
    selectedBackgroundColor = selectedNodeBackgroundColor;
    nodeColor = selectedColor;
  }

  let backgroundColor = isSelected ? selectedBackgroundColor : undefined;

  if (allChildrenSelected) backgroundColor = selectedBackgroundColor;

  if (isRenaming) backgroundColor = undefined;

  const eventType = diff?.eventType;

  if (isDiffing && eventType) {
    backgroundColor = getBackgroundColor(eventType);
  }

  if (isSelected && node.__typename !== 'AutoShape' && !isAutoShapeChild)
    nodeColor = selectedColor;

  if (isDiffing && eventType) {
    nodeColor = getTextColor(eventType);
  }

  const { t } = useTranslation(['common', 'project']);
  const { isProtan } = useUserTenant();

  const nodeStyle = {
    width: '100%',
    paddingTop: 4,
    paddingBottom: 4,
    borderRadius: 4,
    cursor: 'pointer',
  };

  const nodeHoverStyle = {
    backgroundColor: hoverBackgroundColor,
  };

  const contextMenuItems = useMemo(() => {
    const nodeType:
      | 'shape'
      | 'sheetShape'
      | 'folder'
      | 'autoShape'
      | 'unknown' = (() => {
      switch (node.__typename) {
        case 'Shape':
          return 'shape';
        case 'SheetShape':
          return 'sheetShape';
        case 'ShapeFolder':
          return 'folder';
        case 'AutoShape':
          return 'autoShape';
        default:
          return 'folder';
      }
    })();

    const isAutoShapeShape = !!(
      node as ShapeDeepFragment | SheetShapeDeepFragment
    ).autoShapeId;

    const items = [
      {
        icon: faCogs,
        visible: nodeType === 'autoShape',
        label: 'Auto Shape settings',
        onClick: () => {
          console.log('Auto Shape settings');
        },
      },
      {
        icon: faObjectGroup,
        // This item should be visible as long as you are part of the selection
        visible:
          isSelected &&
          nodeType !== 'autoShape' &&
          !isProtan &&
          !isAutoShapeShape,
        label: t('project:shapes.new-folder.create'),
        onClick: groupShapesIntoFolder,
      },
      /*
      {
        icon: faObjectUngroup,
        visible: nodeType === 'folder' && !isProtan,
        label: t('project:shapes.ungroup-folder'),
        onClick: async () => {
          await deleteFolder(node.id);
        },
      },
      */
      {
        icon: faObjectUngroup,
        visible:
          nodeType !== 'folder' &&
          !!(node as ShapeDeepFragment | SheetShapeDeepFragment).folder &&
          !isProtan,
        label: t('project:shapes.remove-from-folder'),
        onClick: async () => await removeShapesFromFolder(node),
      },
      {
        icon: faPencil,
        visible: !isProtan && !isAutoShapeShape,
        label: t('project:shapes.rename-shape'),
        onClick: () => {
          setIsRenaming(true);
        },
      },
      {
        icon: faEyeSlash,
        visible: !isHidden,
        label: t('project:shapes.hide-shape'),
        onClick: () => {
          onHide(node);
        },
      },
      {
        icon: faEye,
        visible: isHidden,
        label: t('project:shapes.show-shape'),
        onClick: () => {
          onShow(node);
        },
      },
      {
        icon: faPen,
        visible:
          nodeType !== 'folder' &&
          nodeType !== 'autoShape' &&
          (node as ShapeDeepFragment | SheetShapeDeepFragment).autoShapeId ===
            null,
        label: t('project:shapes.edit-shape'),
        onClick: () => {
          if (nodeType === 'shape') {
            setViewerMode(ViewerMode.Models);
            editBIMShape(node.id);
          } else {
            const sheetShape = node as SheetShapeDeepFragment;
            setViewerMode(sheetsViewerMode);
            changeActiveSheet(sheetShape.sheetId);
            setPageNumber(sheetShape.sheetPageNumber);
            editSheetShape(node.id);
          }
        },
      },
      {
        icon: faTrash,
        visible: !isAutoShapeShape,
        label: t('project:shapes.delete'),
        onClick: () => {
          onDelete(node);
        },
        color: deleteColor,
      },
    ];

    return items.filter((item) => item.visible);
  }, [
    node,
    isSelected,
    t,
    groupShapesIntoFolder,
    isHidden,
    deleteColor,
    deleteFolder,
    removeShapesFromFolder,
    setIsRenaming,
    onHide,
    onShow,
    setViewerMode,
    editBIMShape,
    changeActiveSheet,
    setPageNumber,
    editSheetShape,
    onDelete,
    isProtan,
    sheetsViewerMode,
  ]);

  const isAutoShape = node.__typename === 'AutoShape';

  return (
    <HStack
      role="group"
      gap={0}
      draggable={isMoveable}
      onDragStart={isMoveable ? onDragStart : undefined}
      style={nodeStyle}
      _hover={isSelected || allChildrenSelected ? undefined : nodeHoverStyle}
      backgroundColor={backgroundColor}
      color={nodeColor}
      opacity={isHidden ? 0.5 : 1}
      paddingLeft={level * 8 + 1}
      paddingRight={isDiffing ? 1 : 0}
      fontWeight={isSelected ? 'bold' : undefined}
      onClick={() => {
        onSelect(node);
      }}
    >
      {children}
      {isRenaming || (
        <>
          {!isDiffing && (
            <Menu
              closeOnSelect={false}
              onClose={() => setIsContextMenuOpen(false)}
              onOpen={() => setIsContextMenuOpen(true)}
            >
              <MenuButton
                as={IconButton}
                aria-label="Select"
                marginLeft={'auto'}
                size={'xs'}
                variant={'unstyled'}
                height={4}
                opacity={0}
                _groupHover={{ opacity: 1 }}
                icon={<SparkelIcon icon={faEllipsis} />}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
              <Portal>
                <MenuList
                  zIndex={99}
                  color={menuItemColor}
                  fontWeight={'normal'}
                >
                  {contextMenuItems.map((item, _idx, _arr) => {
                    const key = `${item.label.toLowerCase()}-${node.id}`;
                    if (
                      item.label === 'Auto Shape settings' &&
                      node.__typename === 'AutoShape'
                    ) {
                      return (
                        <EditAutoshapePopover
                          key={key}
                          autoShape={node}
                          parentIsOpen={isContextMenuOpen}
                          trigger={
                            <MenuItem
                              color={item.color}
                              icon={
                                <SparkelIcon
                                  icon={item.icon}
                                  color={item.color}
                                />
                              }
                            >
                              {item.label}
                            </MenuItem>
                          }
                        />
                      );
                    }
                    return (
                      <MenuItem
                        key={key}
                        color={item.color}
                        icon={
                          <SparkelIcon icon={item.icon} color={item.color} />
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          item.onClick();
                        }}
                      >
                        {item.label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Portal>
            </Menu>
          )}
          {isAutoShape && node.status !== AutoShapeStatus.Completed ? (
            <Tooltip label={node.errorMessage || node.status}>
              {/* Need box to properly pass ref */}
              <Box>
                <AutoShapeStatusIcon status={node.status} />
              </Box>
            </Tooltip>
          ) : (
            <IconButton
              aria-label="show/hide shape"
              marginLeft={isDiffing ? 'auto' : undefined}
              opacity={isHidden ? 1 : 0}
              _groupHover={{ opacity: 1 }}
              size="xs"
              variant={'unstyled'}
              height={4}
              icon={
                <SparkelIcon
                  icon={isHidden ? faEyeSlash : faEye}
                  color={menuItemColor}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                if (isHidden) {
                  onShow(node);
                } else {
                  onHide(node);
                }
              }}
            />
          )}
          {isDiffing && eventType && <DiffBadge eventType={eventType} />}
        </>
      )}
    </HStack>
  );
};

const AutoShapeStatusIcon = ({ status }: { status: AutoShapeStatus }) => {
  switch (status) {
    // Showing completed icon for testing
    case AutoShapeStatus.Completed:
      return <SparkelIcon icon={faCheckCircle} color={'green.400'} />;
    case AutoShapeStatus.Processing:
      return <Spinner size="xs" />;
    case AutoShapeStatus.Failed:
      return <SparkelIcon icon={faTimesCircle} color={'red.400'} />;
    case AutoShapeStatus.Pending:
      return <Spinner size="xs" color={'gray.400'} />; // Different color for testing
    default:
      return null;
  }
};

const IconFromShape = ({
  shape,
}: {
  shape:
    | ShapeDeepFragment
    | SheetShapeDeepFragment
    | ShapeFolderDeepFragment
    | AutoShapeDeepFragment;
}) => {
  if (shape.__typename !== 'Shape' && shape.__typename !== 'SheetShape')
    return null;
  const iconColor = 'gray.400';
  if (shape.__typename === 'Shape') {
    // 3D Shape
    if (shape.polygon)
      return <SparkelIcon icon={faDrawPolygon} color={iconColor} />;
    if (shape.line) return <SparkelIcon as={PolylineIcon} color={iconColor} />;
    if (shape.shapePoint)
      return <SparkelIcon icon={faCircleDot} color={iconColor} />;
    if (shape.extrudedPolygon)
      return <SparkelIcon icon={faCube} color={iconColor} />;
  } else if (shape.__typename === 'SheetShape') {
    // 2D Shape
    const SheetIcon = ({ children }: { children: React.ReactElement }) => (
      <Center
        backgroundColor={'gray.50'}
        borderRadius={'sm'}
        border={'1px solid'}
        borderColor={'gray.300'}
        _dark={{ borderColor: 'gray.600', backgroundColor: 'gray.700' }}
        paddingX={1}
        marginLeft={-1}
        marginRight={-1.5}
        height={5}
      >
        {children}
      </Center>
    );

    if (shape.sheetShapePolygon)
      return (
        <SheetIcon>
          <SparkelIcon icon={faDrawPolygon} color={iconColor} />
        </SheetIcon>
      );
    if (shape.sheetShapeLine)
      return (
        <SheetIcon>
          <SparkelIcon as={PolylineIcon} color={iconColor} />
        </SheetIcon>
      );
    if (shape.sheetShapePoint)
      return (
        <SheetIcon>
          <SparkelIcon icon={faCircleDot} color={iconColor} />
        </SheetIcon>
      );
    if (shape.sheetShapeSplitLine)
      return (
        <SheetIcon>
          <SparkelIcon icon={faChevronDown} color={iconColor} />
        </SheetIcon>
      );
  }

  return <SparkelIcon icon={faDrawPolygon} color={iconColor} />;
};

const NodeItemMemoized = memo(NodeItem);

// eslint-disable-next-line complexity
const MemoizedShapeItem = memo(ShapeItem);
export default MemoizedShapeItem;

//export default ShapeItem;
